import { useEffect, useState } from "react";
import Div from "../../../../components/base/Div";
import Icon from "../../../../components/base/Icon";
import Indicator from "../../../../components/base/Indicator";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import Form from "../../../../components/form/Form";
import TextBox from "../../../../components/form/TextBox";
import { isRoot, useHub } from "../../../../core/Global";
import { FontAwesomeIcons } from "../../../../core/Types";


type Platform = "ios" | "android"

interface IMobileAppRelease {
    platform: Platform
    /** The build number the store is serving. This is what a phone compares itself against. */
    build: number
    /** What that build calls itself. Shown here, never compared. */
    version: string
    storeUrl: string
    /**
     * The name the card gateway knows this app by for Tap to Pay. For iOS, the one in use now;
     * for Android, its own — empty meaning "the same as iOS".
     */
    tapToPayApplicationIdentifier: string
    modifiedAt?: string
}

const PLATFORMS: {
    key: Platform
    name: string
    store: string
    icon: FontAwesomeIcons
    buildLabel: string
    urlPlaceholder: string
}[] = [
        {
            key: "ios",
            name: "iOS",
            store: "App Store",
            icon: "apple",
            buildLabel: "Build number (CFBundleVersion)",
            urlPlaceholder: "https://apps.apple.com/app/id0000000000"
        },
        {
            key: "android",
            name: "Android",
            store: "Google Play",
            icon: "google-play",
            buildLabel: "Build number (versionCode)",
            urlPlaceholder: "https://play.google.com/store/apps/details?id=com.rinvox.app"
        }
    ]


/// What is published on each store, and where to send people to get it.
///
/// Platform-wide and root's alone: raising a build number here retires every older copy of that
/// app. The next time one starts it is told to update, and it will not go further — so the number
/// here is the one actually on the store, not the one being tested.
export default function MobileAppsView() {

    const hub = useHub()

    const [fetching, fetchingSet] = useState<boolean>(true)
    const [working, workingSet] = useState<Platform | undefined>(undefined)
    const [releases, releasesSet] = useState<IMobileAppRelease[]>([])

    const fetch = () => {
        hub.Get("/system/MobileApps/", res => {
            if (res.success) releasesSet(res.data ?? [])
            fetchingSet(false)
        })
    }
    useEffect(fetch, [])

    if (!isRoot()) return <></>

    const release = (platform: Platform) => releases.find(f => f.platform === platform)

    const submit = (platform: Platform, body: any, callback?: (res?: any) => void) => {
        workingSet(platform)

        hub.Post("/system/MobileApps/", {
            platform,
            build: parseInt(body[`${platform}Build`] ?? "0") || 0,
            version: body[`${platform}Version`],
            storeUrl: body[`${platform}StoreUrl`],
            tapToPayApplicationIdentifier: body[`${platform}TapToPayAppId`] ?? ""
        }, res => {
            if (res.success) {
                releasesSet(prev => prev.map(f => f.platform === platform ? res.data : f))
                toastSuccess("Saved!")
            }
            else if (res.field) {
                window.formError(res.field, res.message ?? "An error occurred")
            }
            else {
                toastError(res.message ?? "An error occurred")
            }

            if (callback) callback(res)
            workingSet(undefined)
        })
    }

    return (
        <>
            <div className="card mb-5">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">App Management</h3>
                    </div>
                </div>
                <div className="card-body border-top px-9 pt-8 pb-4">
                    <div className="text-gray-600 fs-7" style={{ maxWidth: 800 }}>
                        The build each store is serving right now. Every mobile app asks for this
                        when it starts and compares it with its own build: anything older is shown
                        an update screen it cannot dismiss, and cannot be used until it is updated.
                        <div className="mt-2">
                            Put in the build that is <span className="fw-bold">actually live</span> —
                            raising it here locks out every copy below it, immediately.
                        </div>
                    </div>
                </div>
            </div>

            {
                fetching ?
                    <div className="my-20"><Indicator /></div>
                    :
                    PLATFORMS.map(p => {
                        const item = release(p.key)

                        return (
                            <Form key={p.key} className="form"
                                onSubmit={(body, callback) => submit(p.key, body, callback)}>
                                <div className="card mb-5">
                                    <div className="card-header border-0">
                                        <div className="card-title m-0">
                                            <h3 className="fw-bolder m-0 d-flex align-items-center">
                                                <Icon icon={p.icon} type="brands" className="me-3" />
                                                {p.name}
                                            </h3>
                                        </div>
                                        <div className="card-action">
                                            <button type="submit" className="btn btn-sm btn-primary"
                                                disabled={working !== undefined}>
                                                {
                                                    working === p.key ?
                                                        <>
                                                            <Icon icon="spinner-third" spin />
                                                            <span className="ms-2">Saving...</span>
                                                        </>
                                                        : <>Save</>
                                                }
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body border-top px-9 pt-10 pb-4">
                                        <div style={{ maxWidth: 800 }}>

                                            <TextBox id={`txt-${p.key}-build`} name={`${p.key}Build`}
                                                label={p.buildLabel} block labelUp numeric
                                                value={item?.build ? String(item.build) : ""} />

                                            <div className="text-gray-600 fs-7 mb-8">
                                                Leave this at 0 and nothing is ever blocked.
                                            </div>

                                            <TextBox id={`txt-${p.key}-version`} name={`${p.key}Version`}
                                                label="Version name" block labelUp
                                                placeholder="3.0.8"
                                                value={item?.version} />

                                            <div className="text-gray-600 fs-7 mb-8">
                                                What that build calls itself. Shown here to keep
                                                track; the apps only ever compare the build number.
                                            </div>

                                            <TextBox id={`txt-${p.key}-url`} name={`${p.key}StoreUrl`}
                                                label={`${p.store} link`} block labelUp
                                                placeholder={p.urlPlaceholder}
                                                value={item?.storeUrl} />

                                            <div className="text-gray-600 fs-7 mb-8">
                                                Where the update button sends them. It is the only
                                                button on that screen, so it has to go somewhere.
                                            </div>

                                            <TextBox id={`txt-${p.key}-taptopay-appid`} name={`${p.key}TapToPayAppId`}
                                                label="Tap to Pay application identifier" block labelUp
                                                placeholder={p.key === "android"
                                                    ? `Same as iOS (${release("ios")?.tapToPayApplicationIdentifier || "RINVOX"})`
                                                    : "RINVOX"}
                                                value={item?.tapToPayApplicationIdentifier} />

                                            <div className="text-gray-600 fs-7 mb-8">
                                                {
                                                    p.key === "android" ?
                                                        <>The name the card gateway has registered the Android app under for Tap to Pay. Leave it empty and Android phones are sent the iOS one.</>
                                                        :
                                                        <>The name the card gateway has registered the iPhone app under for Tap to Pay. It must match theirs exactly — no phone can register otherwise. The app reads it every time it starts, so a change needs no release.</>
                                                }
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )
                    })
            }
        </>
    )
}
